import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

const MODEL_SELECTION_COOKIE_NAME = 'braux-selected-models';
const MODEL_SELECTION_CACHE_DAYS = 30;
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * MODEL_SELECTION_CACHE_DAYS;

export const MODEL_OPTIONS = [
  { key: 'openai_latest', provider: 'openai', shortLabel: 'OpenAI', label: 'OpenAI GPT-5.4' },
  { key: 'gemini_pro_latest', provider: 'gemini', shortLabel: 'Gemini', label: 'Gemini 3.1 Pro Preview' },
  { key: 'claude_opus_latest', provider: 'anthropic', shortLabel: 'Anthropic', label: 'Claude Opus latest' },
];

const DEFAULT_SELECTED_MODELS = MODEL_OPTIONS.map((option) => option.key);

function getCookieAttributes() {
  if (typeof window === 'undefined') {
    return '';
  }

  const attributes = [`Max-Age=${COOKIE_MAX_AGE_SECONDS}`, 'Path=/', 'SameSite=Lax'];
  if (window.location.protocol === 'https:') {
    attributes.push('Secure');
  }
  return attributes.join('; ');
}

function readSelectedModelsFromCookie() {
  if (typeof window === 'undefined') {
    return DEFAULT_SELECTED_MODELS;
  }

  try {
    const cookieEntry = document.cookie
      .split('; ')
      .find((entry) => entry.startsWith(`${MODEL_SELECTION_COOKIE_NAME}=`));

    if (!cookieEntry) {
      return DEFAULT_SELECTED_MODELS;
    }

    const rawValue = cookieEntry.split('=').slice(1).join('=');
    const parsed = JSON.parse(decodeURIComponent(rawValue));
    if (!Array.isArray(parsed)) {
      return DEFAULT_SELECTED_MODELS;
    }

    const selectedModels = MODEL_OPTIONS
      .map((option) => option.key)
      .filter((modelKey) => parsed.includes(modelKey));

    return selectedModels.length > 0 ? selectedModels : DEFAULT_SELECTED_MODELS;
  } catch {
    return DEFAULT_SELECTED_MODELS;
  }
}

export function useChatModelSelection() {
  const [selectedModels, setSelectedModels] = useState(readSelectedModelsFromCookie);

  const selectedModelLabels = useMemo(
    () => MODEL_OPTIONS.filter((option) => selectedModels.includes(option.key)).map((option) => option.shortLabel),
    [selectedModels]
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    document.cookie = `${MODEL_SELECTION_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(selectedModels))}; ${getCookieAttributes()}`;
  }, [selectedModels]);

  const handleToggleModel = useCallback((modelKey) => {
    setSelectedModels((current) => {
      if (current.includes(modelKey)) {
        if (current.length === 1) {
          toast.info('Au moins un modèle doit rester sélectionné');
          return current;
        }
        return current.filter((item) => item !== modelKey);
      }

      return MODEL_OPTIONS.map((option) => option.key).filter(
        (key) => current.includes(key) || key === modelKey
      );
    });
  }, []);

  return {
    modelOptions: MODEL_OPTIONS,
    selectedModels,
    selectedModelLabels,
    handleToggleModel,
  };
}
